import { getToken, getValidToken, refreshAccessToken, redirectToLogin } from './auth.js';
import { BFF_URL } from './runtimeEnv.js';
import { reportPaywall } from './paywallSignal.js';
import { getActiveClinicRef, reportAccessVersion } from './clinicSignal.js';

function currentLocale() {
  return (typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) || 'fa';
}

async function executeRequest(token, operationName, query, variables) {
  // The active clinic travels on EVERY request, derived from the `/c/:ref/…` route
  // (docs/CLINIC_MULTI_CLINIC_UX_REV2.md §A1). A header rather than a GraphQL
  // argument because it applies uniformly and cannot be forgotten on one field —
  // and read at call time from the module signal rather than captured, so a switch
  // takes effect on the very next request without re-threading every hook.
  //
  // Empty before the context provider has resolved (the bootstrap query itself, and
  // the whole pre-membership flow). The BFF treats an absent header as "infer, as
  // you always did", so that case is exactly today's behaviour.
  const clinicRef = getActiveClinicRef();
  return fetch(BFF_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(clinicRef ? { 'x-active-clinic-id': clinicRef } : {}),
      // The panel's language, so the BFF can answer in it: catalogue names, the
      // sentences it composes and its error messages. `<html lang>` is set by
      // `applyDocumentLocale` on every switch, so this is read per request.
      'x-parsina-locale': currentLocale(),
    },
    body: JSON.stringify({ ...(operationName ? { operationName } : {}), query, variables }),
  });
}

/**
 * Raise the app-wide paywall dialog for an entitlement failure.
 *
 * The BFF answers a blocked write with `SUBSCRIPTION_REQUIRED` rather than
 * `FORBIDDEN` precisely so the client can offer a renew button instead of
 * telling a clinic owner they lack access to their own clinic — but nothing was
 * reading the distinction, so every blocked booking surfaced as a generic toast
 * with nowhere to go. Reported from here because the fetch layer is the only
 * place every mutation passes through.
 *
 * The error still throws: the calling screen keeps its own failure handling
 * (leave the form filled, re-enable the button). This only adds the explanation.
 */
function notePaywall(err) {
  if (err?.extensions?.code !== 'SUBSCRIPTION_REQUIRED') return;
  reportPaywall({ ...err.extensions, message: err.message });
}

async function bffRequest(query, variables = {}, operationName = '') {
  const token = await getValidToken();
  if (!token) {
    await redirectToLogin();
    throw new Error('Not authenticated');
  }

  let res = await executeRequest(token, operationName, query, variables);

  // On 401, try to refresh once and retry — handles server-side expiry / clock skew
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      await redirectToLogin();
      throw new Error('Session expired');
    }
    const newToken = getToken();
    res = await executeRequest(newToken, operationName, query, variables);
  }

  if (!res.ok) {
    // Read the body before giving up. Apollo sends a perfectly normal GraphQL error
    // payload alongside a non-2xx status, and throwing on the status alone dropped
    // `extensions` — which is the only thing callers can branch on. App.jsx's
    // `err.extensions?.code === 'UNAUTHENTICATED'` check could therefore never
    // match, so a rejected token surfaced as a generic failure and the app decided
    // the user simply had no clinic, showing the onboarding wizard to clinic owners
    // whose only problem was a stale token.
    const body = await res.json().catch(() => null);
    const gqlError = body?.errors?.[0];
    const err = new Error(gqlError?.message || `BFF error: ${res.status}`);
    err.status = res.status;
    if (gqlError?.extensions) err.extensions = gqlError.extensions;
    notePaywall(err);
    throw err;
  }

  const { data, errors } = await res.json();
  // A single-field call (the common case) that errors still comes back with `data`
  // present but every field null — treat that the same as no data at all.
  const allFieldsNull = data && Object.values(data).every(v => v == null);
  if (errors?.length && (!data || allFieldsNull)) {
    const err = new Error(errors[0].message);
    err.extensions = errors[0].extensions;
    notePaywall(err);
    throw err;
  }
  if (errors?.length) errors.forEach(e => console.warn('BFF partial error:', e.message));

  // A response that carried the caller's own access stamp is the cheapest possible
  // way to notice a role change mid-session: no polling, no websocket, no token
  // introspection. The provider compares it and offers «بازخوانی» when it moves.
  if (data?.myAccess?.version) reportAccessVersion(data.myAccess.version);

  return data;
}

export const bffQuery    = bffRequest;
export const bffMutation = bffRequest;
